import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import DigitalSignatureQualifiedGateway from "../../../api/gateways/DigitalSignatureQualifiedGateway";
import useBaseRequest from "../../../api/BaseRequest";
import { useDispatch } from "react-redux";
import { qdsDocumentsActions } from "../../../store/qdsDocuments/QdsDocumentReducer";
import { extractQueryParamValue } from "../utils/QueryParams";

export const useVerifyQdsQueryParamsAndConfirmEmail = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isError, setIsError] = useState(false);

  const { execute: getWebQdsSigningDocument } = useBaseRequest(
    (data: { email: string; token: string }) =>
      DigitalSignatureQualifiedGateway.getQdsSignatureById(data),
    {
      onCompleted: (response) => {
        const qdsSignatureData = response.data;
        const document = qdsSignatureData?.qdsRequest?.document;
        
        // Get last file from document.files array
        const files = document?.files || [];
        const lastFileIndex = files.length - 1;
        const fileUrl = files[lastFileIndex]?.url || "";

        // Store data in Redux
        dispatch(qdsDocumentsActions.setQdsDocumentBlobUrl(fileUrl));
        dispatch(qdsDocumentsActions.setFetchedQdsDocument({
          qdsSignature: qdsSignatureData,
          qdsRequest: qdsSignatureData?.qdsRequest,
          document: document,
        }));
      },
      onError: async (error) => {
        let errorMessage = error.message;

        // Try to extract message from blob
        if (error?.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const json = JSON.parse(text);
            errorMessage = json?.message || json?.error || error.message;
          } catch (e) {
            console.warn("Failed to parse error blob", e);
          }
        }

        if (errorMessage === "Zahtev za kvalifikovanim digitalnim potpisom je već potpisan!") {
          setIsError(false);
          dispatch(qdsDocumentsActions.setQdsDocumentSuccessfullySignedStatus(true));
          return;
        }

        setIsError(true);
      },
    }
  );

  // Get loading state from getWebQdsSigningDocument
  const isLoading = false;

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = extractQueryParamValue("token");
    const email = extractQueryParamValue("email");
    const qdsSignatureId = extractQueryParamValue("id");

    const keys = Array.from(searchParams.keys());
    const expectedKeys = ["token", "email", "id"];

    const isValidParams =
      keys.length === expectedKeys.length && expectedKeys.every((key) => keys.includes(key));

    if (!isValidParams || !token || !email || !qdsSignatureId) {
      setIsError(true);
      return;
    }

    // Directly fetch the document without confirm step
    getWebQdsSigningDocument({
      email: email || "",
      token: token || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return { isLoading, isError };
};
